import { useCallback, useMemo, useState } from "react";
import { Head, router } from "@inertiajs/react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/Button";
import {
  plan_bottom_up_by_month_channel_path,
  plan_bottom_up_by_month_edit_path,
  plan_bottom_up_by_month_export_path,
  plan_bottom_up_by_month_index_path,
  plan_bottom_up_by_month_tasks_path,
} from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import ProductSearchInput from "./components/ProductSearchInput";
import { matchesProductSearch } from "./utils/productSearch";
import BottomUpGrid from "./components/BottomUpGrid";
import type { WholesaleForecastData } from "./components/ForecastAccountsCell";
import type { ScopeNode } from "@/components/ChannelMenu";
import ByMonthChannelTabs from "./components/ByMonthChannelTabs";
import type { PlanSwitcherData } from "../TopDown/components/PlanSwitcher";
import { monthRangeLabel } from "../TopDown/components/periodLabel";
import PlanHeader from "../components/PlanHeader";
import PlanNavRow from "../components/PlanNavRow";
import ConnectedTaskLine, { type CycleTask } from "../components/ConnectedTaskLine";
import ByMonthToolbarActions from "./components/ByMonthToolbarActions";
import PricingNotice from "./components/PricingNotice";
import StickyBar from "./components/StickyBar";
import type { Row, AvailablePlanNode, Channel } from "./byMonthTypes";

const noop = () => {};

interface Props {
  plan: {
    id: string;
    name: string;
    slug: string;
    begin_date: string;
    end_date: string;
    currency: string | null;
    revenue_target: number;
    org_node: { id: string; name: string };
    // The plan period a revision's numbers cover; the months before it are
    // actual sales. Present for every version, only narrower than
    // begin_date–end_date for a revision.
    plan_period: { start: string; end: string };
    // Number of months at the start of the plan already covered by actual
    // sales. Null for a plain version.
    actuals_months: number | null;
  };
  version: { id: string; version_number: number };
  // First ISO date (YYYY-MM-01) that is still editable — the later of the
  // current month and the version's plan period start.
  locked_before: string;
  months: string[];
  rows: Row[];
  available_plan_nodes: AvailablePlanNode[];
  scope_tree: ScopeNode[];
  current_channel: Channel;
  can: {
    edit: boolean;
    create_task: boolean;
    export: boolean;
    import: boolean;
    view_gross_margin: boolean;
  };
  plan_switcher: PlanSwitcherData;
  // Not sent since 2026-09-14: wholesale channels plan in Forecast by Account and are off this page.
  wholesale_forecast?: WholesaleForecastData | null;
  // The open monthly task for this plan + channel, shown as a one-line recap.
  cycle_task: CycleTask | null;
}

// Read-only view of one channel's month cells. Editing happens on the
// separate edit page (see ByMonthEdit.tsx), reached through the
// "Work on Monthly Plan" button.
export default function ByMonth({
  plan,
  locked_before,
  months,
  rows,
  available_plan_nodes,
  scope_tree,
  current_channel,
  can,
  plan_switcher,
  wholesale_forecast,
  cycle_task,
}: Props) {
  const market = useMarket();
  const targetPeriodLabel = plan.actuals_months
    ? monthRangeLabel(plan.plan_period.start, plan.plan_period.end)
    : null;
  const [searchQuery, setSearchQuery] = useState("");
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [creatingTask, setCreatingTask] = useState<boolean>(false);

  const exportHref = plan_bottom_up_by_month_export_path(market.slug, plan.slug, current_channel.slug);
  const editPath = can.edit
    ? plan_bottom_up_by_month_edit_path(market.slug, plan.slug, current_channel.slug)
    : null;

  const visibleRows = useMemo(
    () => rows.filter((r) => matchesProductSearch(r, searchQuery)),
    [rows, searchQuery],
  );

  // Guarded so a double-click cannot create two open tasks.
  const createTask = () =>
    router.post(plan_bottom_up_by_month_tasks_path(market.slug, plan.slug, current_channel.slug), {}, {
      onStart: () => setCreatingTask(true),
      onFinish: () => setCreatingTask(false),
    });

  const hrefForChannel = useCallback(
    (slug: string) => plan_bottom_up_by_month_channel_path(market.slug, plan.slug, slug),
    [market.slug, plan.slug],
  );

  return (
    <AppLayout
      flush
      header={
        <PlanHeader
          name={plan.name}
          orgNodeName={plan.org_node.name}
          revenueTarget={plan.revenue_target}
          currency={plan.currency}
          beginDate={plan.begin_date}
          endDate={plan.end_date}
        />
      }
    >
      <Head title={`Bottom-Up — ${plan.name}`} />
      <div>
        <PlanNavRow
          planSwitcher={plan_switcher}
          redirectPath={plan_bottom_up_by_month_index_path(market.slug)}
          className="mx-6 mt-5 mb-4"
        />
        <div className="mx-6 mb-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div />
          <PricingNotice>
            You are viewing the monthly plan for <span className="font-medium">{current_channel.name}</span>.
            {cycle_task && <ConnectedTaskLine task={cycle_task} />}
          </PricingNotice>
          <div className="flex items-center justify-self-end gap-2">
            {can.create_task && (
              <Button variant="outline" size="sm" disabled={creatingTask} onClick={createTask}>
                Create task
              </Button>
            )}
            {editPath && (
              <Button size="sm" onClick={() => router.visit(editPath)}>
                Work on Monthly Plan
              </Button>
            )}
          </div>
        </div>
        <div
          style={{ background: "var(--background)", borderColor: "var(--border)" }}
          className="border-y"
        >
          <StickyBar onHeightChange={setToolbarHeight} className="px-0 pt-0">
            <ByMonthChannelTabs
              scopeTree={scope_tree}
              currentNodeId={current_channel.id}
              hrefForChannel={hrefForChannel}
              trailing={
                <>
                  <ProductSearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    className="h-full"
                    inputClassName="rounded-none border-y-0 focus:ring-0"
                  />
                  <ByMonthToolbarActions
                    canExport={can.export}
                    canImport={false}
                    exportHref={exportHref}
                    onOpenImport={noop}
                    onOpenImportRevenue={noop}
                    buttonClassName="-ml-px rounded-none border-y-0"
                  />
                </>
              }
            />
          </StickyBar>

          <BottomUpGrid
            stickyOffset={toolbarHeight}
            months={months}
            rows={visibleRows}
            planCurrency={plan.currency}
            planNodes={available_plan_nodes}
            editable={false}
            canViewGrossMargin={can.view_gross_margin}
            lockedBefore={locked_before}
            targetPeriodLabel={targetPeriodLabel}
            onCellChange={noop}
            onPasteCells={noop}
            wholesaleForecast={wholesale_forecast}
            monthDividers
          />
        </div>
      </div>
    </AppLayout>
  );
}
